import { useState } from "react";
import { useMutation } from "@apollo/client";
import {
  GET_WEBPAGE_QUERY,
  UPSERT_WEBPAGE_MUTATION,
  WebpageData,
} from "../api/Requests";
import { myToastSave } from "../util/myToast";

export const myPageDataEnum = {
  companyName: "companyName",
  companyTitle: "companyTitle",
  companyDescription: "companyDescription",
  aboutMeTitle: "aboutMeTitle",
  aboutMe: "aboutMe",
  logo: "logo",
  serviceItemsTitle: "serviceItemsTitle",
  serviceItems: "serviceItems",
  otherTitle: "otherTitle",
  otherImg: "otherImg",
  phone: "phone",
  email: "email",
  socialUrl: "socialUrl",
  owner: "owner",
  addressFreeForm: "addressFreeForm",
  backgroundImg: "backgroundImg",
  otherItems: "otherItems",
  // TODO: Change these to sectionXEnabled
  enableSectionOne: "enableSectionOne",
  enableSectionTwo: "enableSectionTwo",
  enableSectionThree: "enableSectionThree",
  ctaLink: "ctaLink",
  ctaText: "ctaText",
  ctaEnabled: "ctaEnabled",
  primaryColor: "primaryColor",
  secondaryColor: "secondaryColor",
};

export interface PageData {
  companyName: string;
  companyTitle: string;
  companyDescription: string;
  aboutMeTitle: string;
  aboutMe: string;
  logo: string;
  serviceItemsTitle: string;
  serviceItems: {
    index: number;
    title: string;
    description: string;
    img: string;
  }[];
  otherTitle: string;
  otherItems: {
    title: string;
    description: string;
  }[];
  email: string;
  phone: string;
  socialUrl: string;
  owner: string;
  addressFreeForm: string;
  otherImg: string;
  backgroundImg: string;
  enableSectionOne: boolean;
  enableSectionTwo: boolean;
  enableSectionThree: boolean;
  ctaLink: string;
  ctaText: string;
  ctaEnabled: boolean;
  primaryColor: string;
  secondaryColor: string;
}

function transformMyPageData(bigMapInput: Map<any, any>) {
  return {
    companyName: bigMapInput.get(myPageDataEnum.companyName)?.value,
    companyTitle: bigMapInput.get(myPageDataEnum.companyTitle)?.value,
    companyDescription: bigMapInput.get(myPageDataEnum.companyDescription)
      ?.value,
    aboutMeTitle: bigMapInput.get(myPageDataEnum.aboutMeTitle)?.value,
    aboutMe: bigMapInput.get(myPageDataEnum.aboutMe)?.value,
    logo: bigMapInput.get(myPageDataEnum.logo)?.value,
    serviceItemsTitle: bigMapInput.get(myPageDataEnum.serviceItemsTitle)?.value,
    serviceItems: bigMapInput.get(myPageDataEnum.serviceItems)?.value,
    otherTitle: bigMapInput.get(myPageDataEnum.otherTitle)?.value,
    otherItems: bigMapInput.get(myPageDataEnum.otherItems)?.value,
    email: bigMapInput.get(myPageDataEnum.email)?.value,
    phone: bigMapInput.get(myPageDataEnum.phone)?.value,
    socialUrl: bigMapInput.get(myPageDataEnum.socialUrl)?.value,
    owner: bigMapInput.get(myPageDataEnum.owner)?.value,
    addressFreeForm: bigMapInput.get(myPageDataEnum.addressFreeForm)?.value,
    otherImg: bigMapInput.get(myPageDataEnum.otherImg)?.value,
    backgroundImg: bigMapInput.get(myPageDataEnum.backgroundImg)?.value,
    enableSectionOne: bigMapInput.get(myPageDataEnum.enableSectionOne)?.value,
    enableSectionTwo: bigMapInput.get(myPageDataEnum.enableSectionTwo)?.value,
    enableSectionThree: bigMapInput.get(myPageDataEnum.enableSectionThree)
      ?.value,
    ctaLink: bigMapInput.get(myPageDataEnum.ctaLink)?.value,
    ctaText: bigMapInput.get(myPageDataEnum.ctaText)?.value,
    ctaEnabled: bigMapInput.get(myPageDataEnum.ctaEnabled)?.value,
    primaryColor: bigMapInput.get(myPageDataEnum.primaryColor)?.value,
    secondaryColor: bigMapInput.get(myPageDataEnum.secondaryColor)?.value,
  };
}

export function readOnlyWebpageData(readOnlyData: PageData) {
  const bigMapInput = new Map();

  bigMapInput.set(myPageDataEnum.companyName, {
    value: readOnlyData.companyName,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.companyTitle, {
    value: readOnlyData.companyTitle ?? readOnlyData.companyName,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.companyDescription, {
    value: readOnlyData.companyDescription,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.aboutMeTitle, {
    value: readOnlyData.aboutMeTitle,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.aboutMe, {
    value: readOnlyData.aboutMe,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.logo, {
    value: readOnlyData.logo,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.serviceItemsTitle, {
    value: readOnlyData.serviceItemsTitle,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.serviceItems, {
    value: readOnlyData.serviceItems ?? [],
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.otherTitle, {
    value: readOnlyData.otherTitle,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.otherItems, {
    value: readOnlyData.otherItems ?? [],
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.email, {
    value: readOnlyData.email,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.phone, {
    value: readOnlyData.phone,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.socialUrl, {
    value: readOnlyData.socialUrl,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.owner, {
    value: readOnlyData.owner,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.addressFreeForm, {
    value: readOnlyData.addressFreeForm,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.otherImg, {
    value: readOnlyData.otherImg,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.backgroundImg, {
    value: readOnlyData.backgroundImg,
    setter: () => {},
  });
  // Enablement of sections
  bigMapInput.set(myPageDataEnum.enableSectionOne, {
    value: readOnlyData.enableSectionOne ?? false,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.enableSectionTwo, {
    value: readOnlyData.enableSectionTwo ?? false,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.enableSectionThree, {
    value: readOnlyData.enableSectionThree ?? false,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.ctaLink, {
    value: readOnlyData.ctaLink,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.ctaText, {
    value: readOnlyData.ctaText,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.ctaEnabled, {
    value: readOnlyData.ctaEnabled,
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.primaryColor, {
    value: readOnlyData.primaryColor ?? "#F7F7F7",
    setter: () => {},
  });
  bigMapInput.set(myPageDataEnum.secondaryColor, {
    value: readOnlyData.secondaryColor ?? "#000000",
    setter: () => {},
  });
  return bigMapInput;
}

export function useWebpageData(inputData?: PageData) {
  const [webpageCreate, { loading, error }] = useMutation<WebpageData>(
    UPSERT_WEBPAGE_MUTATION,
  );
  const myPageData = new Map();
  // Company Info

  const [companyName, setCompanyName] = useState(inputData?.companyName);
  myPageData.set(myPageDataEnum.companyName, {
    value: companyName,
    setter: setCompanyName,
  });
  const [companyTitle, seCompanyTitle] = useState(inputData?.companyTitle);
  myPageData.set(myPageDataEnum.companyTitle, {
    value: companyTitle,
    setter: seCompanyTitle,
  });
  const [companyDescription, setCompanyDescription] = useState(
    inputData?.companyDescription,
  );
  myPageData.set(myPageDataEnum.companyDescription, {
    value: companyDescription,
    setter: setCompanyDescription,
  });

  const [logo, setLogo] = useState(inputData?.logo);
  myPageData.set(myPageDataEnum.logo, { value: logo, setter: setLogo });

  const [aboutMeTitle, setAboutMeTitle] = useState(inputData?.aboutMeTitle);
  myPageData.set(myPageDataEnum.aboutMeTitle, {
    value: aboutMeTitle,
    setter: setAboutMeTitle,
  });
  const [aboutMe, setAboutMe] = useState(inputData?.aboutMe);
  myPageData.set(myPageDataEnum.aboutMe, {
    value: aboutMe,
    setter: setAboutMe,
  });

  // Contact Info
  const [phone, setPhone] = useState(inputData?.phone);
  myPageData.set(myPageDataEnum.phone, { value: phone, setter: setPhone });
  const [email, setEmail] = useState(inputData?.email);
  myPageData.set(myPageDataEnum.email, { value: email, setter: setEmail });
  const [owner, setOwner] = useState(inputData?.owner);
  myPageData.set(myPageDataEnum.owner, { value: owner, setter: setOwner });
  const [addressFreeForm, setAddressFreeForm] = useState(
    inputData?.addressFreeForm,
  );
  myPageData.set(myPageDataEnum.addressFreeForm, {
    value: addressFreeForm,
    setter: setAddressFreeForm,
  });
  const [socialUrl, setSocialUrl] = useState(inputData?.socialUrl);
  myPageData.set(myPageDataEnum.socialUrl, {
    value: socialUrl,
    setter: setSocialUrl,
  });

  // Service Items
  const [serviceItemsTitle, setServiceItemsTitle] = useState(
    inputData?.serviceItemsTitle,
  );
  myPageData.set(myPageDataEnum.serviceItemsTitle, {
    value: serviceItemsTitle,
    setter: setServiceItemsTitle,
  });

  const [serviceItems, setServiceItems] = useState(
    inputData?.serviceItems ?? [],
  );
  myPageData.set(myPageDataEnum.serviceItems, {
    value: serviceItems,
    setter: setServiceItems,
  });

  // Misc Page Data
  const [otherTitle, setOtherTitle] = useState(inputData?.otherTitle);
  myPageData.set(myPageDataEnum.otherTitle, {
    value: otherTitle,
    setter: setOtherTitle,
  });

  const [otherImg, setOtherImg] = useState(inputData?.otherImg);
  myPageData.set(myPageDataEnum.otherImg, {
    value: otherImg,
    setter: setOtherImg,
  });

  const [otherItems, setOtherItems] = useState(inputData?.otherItems ?? []);
  myPageData.set(myPageDataEnum.otherItems, {
    value: otherItems,
    setter: setOtherItems,
  });

  // Advanced page settings
  const [backgroundImg, setBackgroundImg] = useState(inputData?.backgroundImg);
  myPageData.set(myPageDataEnum.backgroundImg, {
    value: backgroundImg,
    setter: setBackgroundImg,
  });

  // Enablement of sections
  const [enableSectionOne, setEnableSectionOne] = useState(
    inputData?.enableSectionOne ?? false,
  );
  myPageData.set(myPageDataEnum.enableSectionOne, {
    value: enableSectionOne,
    setter: setEnableSectionOne,
  });
  const [enableSectionTwo, setEnableSectionTwo] = useState(
    inputData?.enableSectionTwo ?? false,
  );
  myPageData.set(myPageDataEnum.enableSectionTwo, {
    value: enableSectionTwo,
    setter: setEnableSectionTwo,
  });
  const [enableSectionThree, setEnableSectionThree] = useState(
    inputData?.enableSectionThree ?? false,
  );
  myPageData.set(myPageDataEnum.enableSectionThree, {
    value: enableSectionThree,
    setter: setEnableSectionThree,
  });
  const [ctaLink, setCtaLink] = useState(inputData?.ctaLink ?? "");
  myPageData.set(myPageDataEnum.ctaLink, {
    value: ctaLink,
    setter: setCtaLink,
  });
  const [ctaText, setCtaText] = useState(inputData?.ctaText ?? "");
  myPageData.set(myPageDataEnum.ctaText, {
    value: ctaText,
    setter: setCtaText,
  });
  const [ctaEnabled, setCtaEnabled] = useState(inputData?.ctaEnabled ?? false);
  myPageData.set(myPageDataEnum.ctaEnabled, {
    value: ctaEnabled,
    setter: setCtaEnabled,
  });

  const [primaryColor, setPrimaryColor] = useState(
    inputData?.primaryColor ?? "#F7F7F7",
  );
  myPageData.set(myPageDataEnum.primaryColor, {
    value: primaryColor,
    setter: setPrimaryColor,
  });
  const [secondaryColor, setSecondaryColor] = useState(
    inputData?.secondaryColor ?? "#000000",
  );
  myPageData.set(myPageDataEnum.secondaryColor, {
    value: secondaryColor,
    setter: setSecondaryColor,
  });

  function saveMyPageData() {
    // We are refetching the main query to update webpage get object
    // TODO: Look at changing this to only update cache if we remove the path arg on the webpage query
    const saveResponse = webpageCreate({
      variables: {
        input: transformMyPageData(myPageData),
      },
      refetchQueries: [GET_WEBPAGE_QUERY],
    });

    myToastSave(saveResponse);
  }

  return {
    myPageData,
    saveMyPageData,
    loading,
    error,
  };
}
